import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { BadgeBorderColor, CallToActionKind } from '@/types';
import { AttributeStringModel, CartProductModel } from '@/models';

import { NS } from '@/constants';
import { useScreenSizeContext } from '@/components/ScreenSize.context';
import { getAttribute, getAttributeValue, getProductImage, getWineboxContent, getWineboxName } from '@/utils/productUtils';
import { getStorageProductsGroupByIdForCart } from '@/utils/storageUtils';
import { isObjectEmpty, isObjectEqual } from '@/utils/comparisonUtils';
import { formatUrl, getHrefForProductLink } from '@/utils/linkUtils';

import { useAppDispatch, useAppSelector } from '@/redux';
import { showSelectGiftModal } from '@/redux/slices/gui.slice';
import { selectLastOrder } from '@/redux/slices/checkout.slice';
import { editCartWinebox, selectCartContent, updateCart } from '@/redux/slices/cart.slice';
import { addProductToCartFromCheckout } from '@/redux/thunks/checkout/addProductToCartFromCheckout';

import Form from '@/components/common/form/Form';
import Divider from '@/components/common/Divider';
import { IconedLink } from '@/components/common/iconedLinks';
import MillesimaLink from '@/components/common/MillesimaLink';
import BadgeForAdmin from '@/components/product/BadgeForAdmin';
import MillesimaPrice from '@/components/common/price/MillesimaPrice';
import ProductTagList from '@/components/common/badge/ProductTagList';
import Dropdown from '@/components/common/design-system/Dropdown.ds';
import AvailabilityBadge from '@/components/common/badge/AvailabilityBadge';
import CartCustomization from '@/components/checkout/cart/CartCustomization';
import CallToActionContainer from '@/components/common/callToAction/CallToActionContainer';
import { AdminCartItemPromotion } from '@/components/checkout/cart/AdminCartItemPromotion';
import CheckoutCartStorageProduct from '@/components/checkout/cart/CheckoutCartStorageProduct';

import styles from './CartRecap.module.scss';
import { ATTRIBUTES } from '@/constants/product';
import { PRICE } from '@/constants/price';
import { PRODUCT_QTY_MAX } from '@/constants/cart';
import { URLS } from '@/constants/urls';
import { getAvailabilityIndicator } from '@/utils/availabilityIndicatorUtils';

export const CartRecapSection = ({ readOnly = false }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const cartContent = useAppSelector(selectCartContent);
  const lastOrder = useAppSelector(selectLastOrder);
  const isActAsCustomer = useAppSelector(({ admin }) => Boolean(admin.actAsCustomer));
  const storeConf = useAppSelector(({ root }) => root.storeConf);
  const isMobile = useScreenSizeContext().isMobile;

  const router = useRouter();
  const cartStorage = useAppSelector(({ storage }) => storage.cartStorage);
  const lastStorageOrder = useAppSelector(({ storage }) => storage.lastStorageOrder);
  const groupedByIdStorageProducts = getStorageProductsGroupByIdForCart(cartStorage);

  const finalContent = readOnly ? lastOrder : cartContent;
  const finalStorageContent = readOnly ? [lastStorageOrder] : groupedByIdStorageProducts;

  const totalExpedition: Partial<Record<string, CartProductModel[]>> = finalContent?.products.reduce((acc, item) => {
    const key = item.product.item.attributes.in_stock.valueId;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(item);
    return acc;
  }, {});

  const totalStorageExpedition = { L: finalStorageContent };

  if (finalContent.selectedGift) {
    const inStockValueIdFromGift = finalContent.selectedGift.product.item.attributes.in_stock.valueId;
    totalExpedition[inStockValueIdFromGift] = totalExpedition[inStockValueIdFromGift] || [];
    totalExpedition[inStockValueIdFromGift] = [...totalExpedition[inStockValueIdFromGift], finalContent.selectedGift];
  }

  if (Object.keys(totalExpedition).length === 0 && Object.keys(totalStorageExpedition).length === 0) {
    return null;
  }

  const displayOrder = ['L', 'PA', 'P'];
  const sortedTotalExpedition = Object.keys(totalExpedition)
    .sort((a, b) => displayOrder.indexOf(a) - displayOrder.indexOf(b))
    .reduce((acc, key) => {
      acc[key] = totalExpedition[key];
      return acc;
    }, {}) as typeof totalExpedition;

  const handleProductQuantityChange = (partnumber: string) => (value: string) => {
    dispatch(addProductToCartFromCheckout({ partnumber, quantity: value }));
  };

  const gotoProduct = async (cartProduct: CartProductModel) => {
    const product = cartProduct.product;
    const href = getHrefForProductLink(product.seoKeyword, product._id, product.item?.partnumber, product.isSpirit);
    const as: string = formatUrl(product.seoKeyword, product.isSpirit, storeConf);

    await router.push(href, as);
  };

  const getWineboxShortDesc = (winebox: CartProductModel) => (
    <>
      <p
        className={styles.paragraph}
        dangerouslySetInnerHTML={{ __html: getWineboxContent(winebox.products) }}
      />
      {!readOnly && (
        <div className={styles['edit-winebox']} data-test-id="edit-winebox" data-rbf="edit-winebox">
          <IconedLink
            text={t('btn.modify')}
            icon={URLS.EDIT_BLACK}
            onClicked={() => dispatch(editCartWinebox({ boxId: winebox.product.partnumber }))}
            aria-label={t('btn.modify')}
          />
        </div>
      )}
    </>
  );

  const handleSubmit = (data, cartProduct) => {
    const updatedProducts = cartContent.products.map((product) => {
      if (isObjectEqual(product, cartProduct)) {
        return {
          ...product,
          csrPromotionPercent: data.promoPercent !== '' ? Number(data.promoPercent) : null,
          csrPromotionValue: data.promoValue !== '' ? Number(data.promoValue) : null
        };
      }
      return product;
    });

    dispatch(updateCart({
      content: {
        ...cartContent,
        products: updatedProducts
      }
    }));
  };

  const node = (() => {
    const sections = [];
    Object.entries(sortedTotalExpedition).forEach(([key, products], index) => {
      const title = `${t('checkout:cart.shipment')} ${index + 1}/${Object.keys(totalExpedition).length}`;
      const wineExpeditionLabel = t(`checkout:cart.${key}`);
      sections.push(
        <section key={index}>
          <div className={styles['title-container']}>
            <p className={styles.title}>{title}</p>
            <p className={styles.label}>{wineExpeditionLabel}</p>
          </div>
          <div className={styles['products-container']}>
            {products.map((cartProduct: CartProductModel) => {
              const imageUrl = cartProduct.product.item?.image ? cartProduct.product.item.image : cartProduct.product.image;
              const image = getProductImage(imageUrl, cartProduct.wineBox);
              const stock = cartProduct.product.item?.stock;
              const filler = stock >= PRODUCT_QTY_MAX ? PRODUCT_QTY_MAX : stock;
              const dropdownOptions = filler && Array(filler + 1).fill(1).map((_, i) => ({
                value: i.toString(),
                label: i.toString()
              }));

              const attributes = {
                shouldShow: !!(cartProduct.product.attributes &&
                  cartProduct.product.attributes.appellation && cartProduct.product.attributes.couleur),
                label: cartProduct.product.attributes?.appellation?.value ?
                  `${cartProduct.product.attributes?.appellation?.value} - ${cartProduct.product.attributes?.couleur?.value}` :
                  `${cartProduct.product.attributes?.couleur?.value}`,
              };

              const productAvailability = getAvailabilityIndicator(
                t, storeConf.storeId, cartProduct.product, cartProduct.product.item, storeConf.locale
              )[0];
              const isWineBox = cartProduct.wineBox;
              const productName = isWineBox ? t(getWineboxName(cartProduct.isPrimeur)) : cartProduct.product.productName;
              const shortDesc = cartProduct.product.item?.shortdesc;

              const promo = cartProduct.promo?.description;
              const quantity = cartProduct.quantity.toString();
              const itemPromotion: AttributeStringModel = getAttribute(cartProduct.product?.item, ATTRIBUTES.RIBBONAD);

              const promoInfos = {
                shouldShow: (!!cartProduct.discountPrice && cartProduct.discountPrice !== cartProduct.price) || cartProduct.isGift,
                value: cartProduct.discountPrice === 0 ? PRICE.FREE : cartProduct.discountPrice
              };

              const productGiftExtraInfos = {
                shouldShow: cartProduct.isGift && (finalContent.gifts?.length > 1 || finalContent.giftPacks?.length > 1),
                label: t('checkout:cart.changeGift'),
                action: () => dispatch(showSelectGiftModal())
              };

              const infoSiteLabel = getAttributeValue(cartProduct.product?.item, ATTRIBUTES.INFO_SITE);
              const customizationQt = cartProduct.customizationQt;

              return (
                <>
                  <section key={cartProduct.product.item.partnumber} className={styles['product-card']}>
                    <div className={styles['image-container']} onClick={() => gotoProduct(cartProduct)}>
                      <img src={image} alt={productName} className={styles.image} height={150} width={150}/>
                    </div>

                    <section className={styles['infos-container']}>
                      <ProductTagList t={t} promotion={itemPromotion} infoSiteLabel={infoSiteLabel} className={styles.tags} />
                      {isWineBox ? (
                        <div className={styles['product-name']}>{productName}</div>
                      ) : (
                        <MillesimaLink
                          type="inline-link"
                          ariaLabel={productName}
                          innerHtml={productName}
                          size={'medium'}
                          href={getHrefForProductLink(cartProduct.product.seoKeyword,
                            cartProduct.product._id, cartProduct.product.item?.partnumber, cartProduct.product.isSpirit)}
                          as={formatUrl(cartProduct.product.seoKeyword, cartProduct.product.isSpirit, storeConf)}
                        />
                      )}
                      {attributes.shouldShow && (
                        <p className={styles.paragraph}>{attributes.label}</p>
                      )}
                      {promo && (
                        <p className={styles.paragraph}>{promo}</p>
                      )}
                      {cartProduct.wineBox ? getWineboxShortDesc(cartProduct) : (
                        <p className={styles.paragraph}>{shortDesc}</p>
                      )}
                      {isActAsCustomer && (
                        <BadgeForAdmin item={cartProduct.product.item} isWineBox={cartProduct.wineBox} product={cartProduct}/>
                      )}
                      {productGiftExtraInfos.shouldShow && (
                        <div className={styles['select-gift']}>
                          <CallToActionContainer
                            kind={CallToActionKind.GHOST} onClick={productGiftExtraInfos.action}
                            className={styles['select-gift']}
                            icon={{ url: URLS.EDIT_BLACK, alt: 'edit', rightSide: true, width: 16, height: 16 }}
                          >
                            {productGiftExtraInfos.label}
                          </CallToActionContainer>
                        </div>
                      )}
                    </section>

                    <div className={styles['dropdown-and-price-container']}>
                      {readOnly || cartProduct.isGift ? (
                        <div>{`x ${quantity}`}</div>
                      ) : (
                        <Dropdown
                          id={`${cartProduct.product.item.partnumber}-quantity`}
                          defaultValue={quantity}
                          options={dropdownOptions}
                          isFullWidth={false}
                          onChange={handleProductQuantityChange(cartProduct.product.item.partnumber)}
                        />
                      )}
                      <div className={styles['price-infos']}>
                        {promoInfos.shouldShow && (
                          <MillesimaPrice
                            value={promoInfos.value}
                            className={{ container: styles['discount-price'] }}
                          />
                        )}
                        {(cartProduct.isGift && !readOnly) || (!cartProduct.isGift) && (
                          <MillesimaPrice
                            value={cartProduct.price} className={{ container: styles.price }}
                          />
                        )}
                      </div>
                      {isActAsCustomer && !readOnly && (
                        <Form onSubmit={(data) => handleSubmit(data, cartProduct)} defaultValues={{
                          promoPercent: cartProduct.csrPromotionPercent,
                          promoValue: cartProduct.csrPromotionValue
                        }}>
                          <AdminCartItemPromotion />
                        </Form>
                      )}
                    </div>
                    {productAvailability && (
                      <div className={styles['availability-container']}>
                        <Divider spacing={isMobile ? 16 : 24} />
                        <AvailabilityBadge
                          background={productAvailability.color}
                          border={BadgeBorderColor.WHITE}
                          label={productAvailability.message}
                        />
                      </div>
                    )}
                    {customizationQt > 0 && (
                      <CartCustomization cartProduct={cartProduct} readOnly={readOnly} />
                    )}
                  </section>
                </>
              );
            })}
          </div>
        </section>
      );
    });

    if (!isObjectEmpty(groupedByIdStorageProducts) || (readOnly && !isObjectEmpty(lastStorageOrder))) {
      sections.push(
        <>
          <Divider spacing={16}/>
          <span className={styles['client-storage']}>{t('cart.storage', { ns: NS.CHECKOUT })}</span>
          <Divider spacing={16}/>
          {Object.values(totalStorageExpedition).map((storageProduct) => {
            return storageProduct.map((product) => {
              const image = product.storageProducts[0].storageProductForCart.image;
              const finalProduct = readOnly ?
                { storageProducts: { ...product.storageProducts }, ...product.storageProducts[0], image } : product;
              return (
                <CheckoutCartStorageProduct
                  key={product.storageProducts[0].storageProductForCart.productId}
                  product={finalProduct}
                  readOnly={readOnly}
                />
              );
            });
          })}
        </>
      );
    }

    return sections;
  })();

  return <div>
    {node}
  </div>;
};
